import type { RoundAssignment, RoundGroup } from "../../domain/round/types";
import type { RoundWorkspacePayload } from "../../lib/api";

export type LiveTeeGroup = { group: number; tee?: number | null; start: string; finish9?: string; finish18?: string };

const minutes = (value: string | undefined) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || "").trim());
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

export type TeeTimesDiff = {
  changed: number;
  total: number;
  /** Jedno wspólne przesunięcie wszystkich grup (min) albo null, gdy grupy przesunięto różnie. */
  uniformShift: number | null;
  shifts: Map<number, number>;
};

function newTimes(group: RoundGroup, live: LiveTeeGroup) {
  const start = minutes(live.start);
  if (start == null) return null;
  const pace9 = (group.finish9Minutes ?? group.startMinutes + 150) - group.startMinutes;
  const pace18 = (group.finish18Minutes ?? group.startMinutes + 300) - group.startMinutes;
  // Bezpiecznie: późniejszy z czasu DPWT i standardowego tempa z zatwierdzonej rozpiski.
  const f9 = Math.max(minutes(live.finish9) ?? 0, start + pace9);
  const f18 = Math.max(minutes(live.finish18) ?? 0, start + pace18);
  return { start, f9, f18 };
}

export function diffTeeTimes(data: RoundWorkspacePayload, live: LiveTeeGroup[]): TeeTimesDiff {
  const shifts = new Map<number, number>();
  const byNumber = new Map(live.map((row) => [Number(row.group), row]));
  for (const group of data.schedule?.groups || []) {
    const row = byNumber.get(group.number);
    const times = row ? newTimes(group, row) : null;
    if (times) shifts.set(group.number, times.start - group.startMinutes);
  }
  const values = [...shifts.values()];
  return {
    changed: values.filter((value) => value !== 0).length,
    total: values.length,
    uniformShift: values.length && values.every((value) => value === values[0]) ? values[0] : null,
    shifts,
  };
}

const interval = (assignment: RoundAssignment, group: RoundGroup): [number, number] => {
  const start = group.startMinutes, f9 = group.finish9Minutes ?? start, f18 = group.finish18Minutes ?? start;
  if (assignment.segment === "holes1to9" || assignment.segment === "extra1to9") return [start, f9];
  if (assignment.segment === "holes10to18" || assignment.segment === "extra10to18") return [f9, f18];
  return [start, f18];
};

/** Kopia workspace z aktualnymi tee times (tylko do PNG/Excel; niczego nie zapisuje w bazie). */
export function applyTeeTimes(data: RoundWorkspacePayload, live: LiveTeeGroup[]): RoundWorkspacePayload {
  if (!data.schedule) return data;
  const diff = diffTeeTimes(data, live);
  const byNumber = new Map(live.map((row) => [Number(row.group), row]));
  const copy: RoundWorkspacePayload = JSON.parse(JSON.stringify(data));
  const schedule = copy.schedule!;
  schedule.groups = schedule.groups.map((group) => {
    const row = byNumber.get(group.number);
    const times = row ? newTimes(group, row) : null;
    return times ? { ...group, startMinutes: times.start, finish9Minutes: times.f9, finish18Minutes: times.f18 } : group;
  });
  const groups = new Map(schedule.groups.map((group) => [group.id, group]));
  schedule.people = schedule.people.map((person) => {
    const spans = schedule.assignments.filter((a) => a.personId === person.personId).map((a) => interval(a, groups.get(a.groupId)!));
    if (!spans.length) return person;
    const starts = spans.map((span) => span[0]).sort((a, b) => a - b);
    const ends = spans.map((span) => span[1]).sort((a, b) => a - b);
    const ordered = [...spans].sort((a, b) => a[0] - b[0]);
    let longest = 0, active = ordered[0][1];
    for (const [s, e] of ordered.slice(1)) { longest = Math.max(longest, s - active); active = Math.max(active, e); }
    return { ...person, dutyStartMinutes: starts[0], dutyEndMinutes: ends[ends.length - 1], longestBreakMinutes: Math.max(0, longest) };
  });
  // Transport przesuwamy tylko przy jednym wspólnym przesunięciu wszystkich grup.
  if (copy.transport && diff.uniformShift) {
    const shift = diff.uniformShift;
    const move = (trips: NonNullable<typeof copy.transport>["departures"]) => trips.map((trip) => ({ ...trip, departAtMinutes: trip.departAtMinutes + shift, arriveAtMinutes: trip.arriveAtMinutes + shift }));
    copy.transport = { ...copy.transport, departures: move(copy.transport.departures), returns: move(copy.transport.returns) };
  }
  return copy;
}
